import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash-es';
import { faMagnifyingGlass } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { Button } from '@components/main-components/button/Button';
import { FeedPostModalSoundSelectItem } from '@components/modals/FeedPostModal/FeedPostModalSoundSelectItem';
import { FeedPostModalSoundSelectItemSkeleton } from '@components/modals/FeedPostModal/FeedPostModalSoundSelectItemSkeleton';
import type { FeedPostModalMainSoundFragment } from '@graphql/reactQuery';
import {
  FeedPostModalSoundSelectItemFragmentDoc,
  getFragment,
  SearchAllSoundsDocument,
  useQuery,
} from '@graphql/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  query SearchAllSounds($input: SearchInput!) {
    search(input: $input) {
      id
      releasesPaginated {
        edges {
          node {
            ...FeedPostModalSoundSelectItem
            ...ReleaseCoverImagePlayButton
            ...FeedPostModalMainSound
          }
        }
      }
    }
  }
`);

export function FeedPostModalSoundSelect({
  onBackClicked,
  onReleaseSelected,
}: {
  onBackClicked: () => void;
  onReleaseSelected: (release: FeedPostModalMainSoundFragment) => void;
}) {
  const [searchText, setSearchText] = React.useState('');
  const [selectedRelease, setSelectedRelease] = useState<FeedPostModalMainSoundFragment>();
  const [debouncedSearchText, setDebounceSearchText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSetFn = useMemo(() => {
    return debounce(setDebounceSearchText, 250, { leading: true, maxWait: 2000 });
  }, []);

  const { data, isLoading: isLoadingQuery } = useQuery(SearchAllSoundsDocument, {
    variables: {
      input: {
        limit: 20,
        text: debouncedSearchText,
      },
    },
    staleTime: Infinity,
    enabled: Boolean(debouncedSearchText.length),
  });

  const isLoading = isLoadingQuery || searchText !== debouncedSearchText;

  const edges = data?.data.search.releasesPaginated.edges;
  const noResults = !isLoading && !Boolean(edges?.length) && Boolean(debouncedSearchText.length);

  /**
   * Autofocus text after animation
   * (which is why we can't do autoFocus={true})
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 600);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="relative flex h-full w-full flex-col">
      {/**
       * Search bar
       */}
      <div className="mx-4 box-border flex items-center gap-3 self-stretch rounded-md bg-base100 px-3 lt:mx-6">
        <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" className="text-base800" />
        <input
          ref={inputRef}
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            debouncedSetFn(e.target.value);
          }}
          placeholder="Search your sounds"
          className="w-full bg-transparent py-2 text-base-m text-base900 focus:outline-none"
        />
      </div>

      {/**
       * The sound select item scroll view
       */}
      <div className="flex flex-1 flex-col overflow-scroll py-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, idx) => (
              <FeedPostModalSoundSelectItemSkeleton key={`sound-select-item-skeleton-${idx}`} />
            ))
          : edges?.map(({ node }) => {
              const release = getFragment(FeedPostModalSoundSelectItemFragmentDoc, node);
              return (
                <FeedPostModalSoundSelectItem
                  key={`feed-post-modal-sound-select-item-${release.id}`}
                  releaseFrag={node}
                  selected={release.id === selectedRelease?.id}
                  onReleaseSelected={release => {
                    setSelectedRelease(release);
                  }}
                />
              );
            })}

        {noResults && (
          <div className="mx-4 flex flex-col items-start justify-start lt:mx-6">
            <div className="text-base-s text-base500">
              {`Nothing sounds like "${debouncedSearchText}"`}{' '}
            </div>
          </div>
        )}
      </div>

      {/**
       * Bottom buttons
       */}
      <div className="flex flex-row gap-2 bg-white px-4 lt:px-6">
        <Button
          variant="secondary"
          fullWidth
          className="flex-1"
          label="Back"
          onClick={onBackClicked}
        />
        <Button
          variant="primary"
          fullWidth
          className="flex-1"
          label="Select"
          disabled={!Boolean(selectedRelease)}
          onClick={() => {
            // don't really need this because the button is disabled
            // when there is no selected release
            // but just in case
            if (selectedRelease) {
              onReleaseSelected(selectedRelease);
              onBackClicked();
            }
          }}
        />
      </div>
    </div>
  );
}
